import { getCardStatus, getUserCards, getCreditInformation } from "../api/api";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { useCreditStore } from "../stores/creditStore";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { useTransactionsStore } from "../stores/transactionsStore";

export const BASE_CHAIN_ID = 8453; // Base chain ID
export const HYPER_EVM_CHAIN_ID = 999; // HyperEVM chain ID

class DataFetcher {
  private static instance: DataFetcher;
  private hasFetched = false;

  static getInstance(): DataFetcher {
    if (!DataFetcher.instance) {
      DataFetcher.instance = new DataFetcher();
    }
    return DataFetcher.instance;
  }

  async fetchAllData(): Promise<void> {
    const { eoaAddress, chainID, isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized || !eoaAddress || !chainID || this.hasFetched) {
      return;
    }

    // Validate supported chain ID
    if (![BASE_CHAIN_ID, HYPER_EVM_CHAIN_ID].includes(chainID)) {
      throw new Error(
        `Unsupported chain ID: ${chainID}. Supported chain IDs are ${BASE_CHAIN_ID} and ${HYPER_EVM_CHAIN_ID}.`
      );
    }

    console.log("🚀 SDK: Starting data fetch for:", eoaAddress);

    const cardStore = useCardStatusStore.getState();
    const creditStore = useCreditStore.getState();

    // Set loading states
    cardStore._setLoading(true);
    creditStore._setLoading(true);

    try {
      // Always fetch card status and user cards
      const [cardStatusResponse, userCardsResponse] = await Promise.all([
        getCardStatus(eoaAddress),
        getUserCards(eoaAddress),
      ]);

      // Initialize credit info containers
      let aaveCreditResponse = null;
      let eulerCreditResponse = null;
      let hypurrFiCreditResponse = null;

      // Conditionally fetch credit info based on chain
      if (chainID === BASE_CHAIN_ID) {
        [aaveCreditResponse, eulerCreditResponse] = await Promise.all([
          getCreditInformation(eoaAddress, "aave-v3"),
          getCreditInformation(eoaAddress, "euler"),
        ]);
      } else if (chainID === HYPER_EVM_CHAIN_ID) {
        hypurrFiCreditResponse = await getCreditInformation(
          eoaAddress,
          "hypurrFi"
        );
      }

      // Update card data
      const userCard = userCardsResponse.data?.[0] || null;
      cardStore._setCardStatus(cardStatusResponse.data, userCard);

      // Update credit info conditionally
      if (aaveCreditResponse) {
        creditStore._setCreditInfo("aave", aaveCreditResponse);
      }
      if (eulerCreditResponse) {
        creditStore._setCreditInfo("euler", eulerCreditResponse);
      }
      if (hypurrFiCreditResponse) {
        creditStore._setCreditInfo("hypurrFi", hypurrFiCreditResponse);
      }

      this.hasFetched = true;
      console.log("✅ SDK: Successfully fetched all data for:", eoaAddress);
    } catch (error) {
      console.error("❌ SDK: Error fetching data:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch data";

      cardStore._setError(errorMessage);
      creditStore._setError(errorMessage);
    } finally {
      cardStore._setLoading(false);
      creditStore._setLoading(false);
    }
  }

  // Public method to trigger data fetching after authentication
  async fetchDataAfterAuth(): Promise<void> {
    // Reset the fetch flag to allow fetching again
    this.hasFetched = false;
    await this.fetchAllData();
  }

  reset(): void {
    this.hasFetched = false;
    useCardStatusStore.getState()._reset();
    useCreditStore.getState()._reset();
    useTransactionsStore.getState()._reset();
  }
}

export const dataFetcher = DataFetcher.getInstance();
